import { useMemoizedFn } from 'ahooks';
import dayjs, { type Dayjs } from 'dayjs';
import { useRef, useState, startTransition, useMemo } from 'react';
import type { IListItem } from '../interface.js';
import type { WindowVirtualizerHandle } from 'virtua';

const usePlanDetailScroll = ({
  startTime,
  endTime,
}: {
  startTime: Dayjs;
  endTime: Dayjs;
}) => {
  const dayTitleRef = useRef<Record<string, number>>({});
  const ref = useRef<WindowVirtualizerHandle>(null);

  const [date, setDate] = useState(() => {
    return '总览';
  });

  const onNavListChange = useMemoizedFn(
    (payload: string, options?: { smooth?: boolean; delay?: number }) => {
      setDate(payload);

      setTimeout(() => {
        let scrollIndex = dayTitleRef.current[payload];

        if (payload === '总览') {
          scrollIndex = -1;
        }

        if (scrollIndex !== undefined) {
          ref.current?.scrollToIndex(scrollIndex, {
            smooth: options?.smooth ?? true,
            offset: -170,
          });
        }
      }, options?.delay ?? 0);
    }
  );

  const isInitScroll = useRef(false);
  const onScroll = useMemoizedFn(() => {
    if (date === '总览' || isInitScroll.current) return;
    startTransition(() => {
      if (!ref.current) return;
      const range = [ref.current.findStartIndex(), ref.current.findEndIndex()];
      const currentDate = Object.entries(dayTitleRef.current).findLast(
        ([key, value]) => value < range[1]
      )?.[0];

      if (currentDate && currentDate !== date) setDate(currentDate);
    });
  });

  const onPlanDetailLoaded = useMemoizedFn((res: IListItem[] | undefined) => {
    dayTitleRef.current = {};
    res?.forEach((i, index) => {
      if (i.type === 'DayTitle') {
        dayTitleRef.current[i.props.date] = index;
      }
    });
    handleGoDay();
  });

  const hasInit = useRef(false);
  const handleGoDay = useMemoizedFn(() => {
    if (hasInit.current) return;
    hasInit.current = true;
    const curDate = +new Date();
    if (curDate > +startTime) {
      isInitScroll.current = true;

      onNavListChange(dayjs(Math.min(curDate, +endTime)).format('YYYY-MM-DD'), {
        smooth: false,
        delay: 100,
      });
    }
  });

  const navListProps = useMemo(
    () => ({
      value: date,
      onChange: onNavListChange,
      startTime,
      endTime,
    }),
    [date, startTime, endTime]
  );
  const scrollContainerProps = useMemo(
    () => ({
      onScroll,
      onScrollEnd: () => {
        isInitScroll.current = false;
      },
      onRef: ref,
    }),
    []
  );

  return {
    date,
    onPlanDetailLoaded,
    navListProps,
    scrollContainerProps,
  };
};

export default usePlanDetailScroll;
